import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../admin/FirebaseConfig';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    // Clear error when user starts typing
    if (formErrors[e.target.id]) {
      setFormErrors({ ...formErrors, [e.target.id]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.message.trim()) errors.message = 'Message is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      await addDoc(collection(firestore, 'inquiries'), {
        ...formData,
        timestamp: serverTimestamp()
      });

      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setFormErrors({});
      
      // Auto hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error adding document: ', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid min-vh-100 py-5" style={{ 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Wireframe Background with Geometric Patterns */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          linear-gradient(90deg, rgba(139, 0, 0, 0.03) 1px, transparent 1px),
          linear-gradient(rgba(139, 0, 0, 0.03) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        zIndex: 0
      }}></div>
      
      {/* Decorative elements */}
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139, 0, 0, 0.05) 0%, transparent 70%)',
        top: '-150px',
        right: '-150px',
        zIndex: 0
      }}></div>
      
      <div style={{
        position: 'absolute',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(139, 0, 0, 0.03) 0%, transparent 70%)',
        bottom: '-100px',
        left: '-100px',
        zIndex: 0
      }}></div>

      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* Header Section */}
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3" style={{ 
            color: '#8B0000',
            background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Get In Touch
          </h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            We're here to assist you with any inquiries. Our dedicated team is committed to providing excellent service.
          </p>
          <div className="mx-auto" style={{ 
            height: '4px', 
            width: '80px', 
            background: 'linear-gradient(to right, #8B0000, #ffd700, #8B0000)',
            borderRadius: '2px'
          }}></div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="row gy-4">
              {/* Column 1: Contact Details and Content */}
              <div className="col-12 col-md-5">
                <div className="card border-0 shadow-sm h-100" style={{ 
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                }}>
                  <div className="card-body p-4 p-md-5">
                    <h3 className="mb-4" style={{ color: '#8B0000' }}>
                      <i className="fas fa-map-marker-alt me-2"></i>
                      Our Offices
                    </h3>
                    
                    <div className="mb-4">
                      <h5 className="fw-semibold mb-3">Jaipur Office</h5>
                      <div className="d-flex mb-2">
                        <div className="me-3">
                          <i className="fas fa-building" style={{ color: '#8B0000', width: '20px' }}></i>
                        </div>
                        <div>
                          <p className="mb-0">C/24, 1st, 2nd, 3rd Floor</p>
                          <p className="mb-0">Ashok Marg, C Scheme</p>
                          <p className="mb-0">Jaipur - 302001</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="fw-semibold mb-3">Mumbai Office</h5>
                      <div className="d-flex mb-2">
                        <div className="me-3">
                          <i className="fas fa-building" style={{ color: '#8B0000', width: '20px' }}></i>
                        </div>
                        <div>
                          <p className="mb-0">One Indiabulls Center, Tower 1</p>
                          <p className="mb-0">18th Floor, Jupiter Mill Compound</p>
                          <p className="mb-0">Senapati Bapat Marg, Elphinstone Road</p>
                          <p className="mb-0">Mumbai - 400013</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h5 className="fw-semibold mb-3">Contact Information</h5>
                      <div className="d-flex mb-2">
                        <div className="me-3">
                          <i className="fas fa-envelope" style={{ color: '#8B0000', width: '20px' }}></i>
                        </div>
                        <div>
                          <p className="mb-0">support@adityabirlafinanceltd.com </p>
                        </div>
                      </div>
                      {/* <div className="d-flex mb-2">
                        <div className="me-3">
                          <i className="fas fa-phone" style={{ color: '#8B0000', width: '20px' }}></i>
                        </div>
                        <div>
                          <p className="mb-0">+91 9842 34 6458</p>
                        </div>
                      </div> */}
                    </div>

                    <div className="mt-5">
                      <h5 className="fw-semibold mb-3" style={{ color: '#8B0000' }}>
                        <i className="fas fa-question-circle me-2"></i>
                        Why Contact Us
                      </h5>
                      <p className="text-muted">
                        We are here to assist you with any inquiries you may have. Our dedicated
                        team is committed to providing excellent service and addressing your needs
                        promptly. Feel free to reach out to us for any information or assistance
                        you require.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Contact Form Card */}
              <div className="col-12 col-md-7">
                <div className="card border-0 shadow-lg h-100" style={{ 
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  overflow: 'hidden'
                }}>
                  <div className="card-header border-0 py-4" style={{
                    background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)'
                  }}>
                    <h3 className="text-white text-center mb-0">
                      <i className="fas fa-paper-plane me-2"></i>
                      Send us a Message
                    </h3>
                  </div>
                  
                  <div className="card-body p-4 p-md-5">
                    {submitSuccess && (
                      <div className="alert alert-success d-flex align-items-center" role="alert" style={{ borderRadius: '10px' }}>
                        <i className="fas fa-check-circle me-2 fs-4"></i>
                        <div>
                          <h6 className="mb-1">Message sent successfully!</h6>
                          <p className="mb-0 small">We'll get back to you as soon as possible.</p>
                        </div>
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <label htmlFor="name" className="form-label fw-semibold">Full Name</label>
                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0">
                            <i className="fas fa-user" style={{ color: '#8B0000' }}></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control py-3 border-start-0 ${formErrors.name ? 'is-invalid' : ''}`}
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            style={{ borderRadius: '0 8px 8px 0' }}
                          />
                        </div>
                        {formErrors.name && <div className="invalid-feedback d-block mt-1">{formErrors.name}</div>}
                      </div>

                      <div className="mb-4">
                        <label htmlFor="email" className="form-label fw-semibold">Email Address</label>
                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0">
                            <i className="fas fa-envelope" style={{ color: '#8B0000' }}></i>
                          </span>
                          <input
                            type="email"
                            className={`form-control py-3 border-start-0 ${formErrors.email ? 'is-invalid' : ''}`}
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="support@adityabirlafinanceltd.com"
                            style={{ borderRadius: '0 8px 8px 0' }}
                          />
                        </div>
                        {formErrors.email && <div className="invalid-feedback d-block mt-1">{formErrors.email}</div>}
                      </div>

                      <div className="mb-4">
                        <label htmlFor="message" className="form-label fw-semibold">Message</label>
                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0 align-items-start pt-3">
                            <i className="fas fa-comment" style={{ color: '#8B0000' }}></i>
                          </span>
                          <textarea
                            className={`form-control py-3 border-start-0 ${formErrors.message ? 'is-invalid' : ''}`}
                            id="message"
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="How can we help you?"
                            style={{ borderRadius: '0 8px 8px 0', resize: 'none' }}
                          ></textarea>
                        </div>
                        {formErrors.message && <div className="invalid-feedback d-block mt-1">{formErrors.message}</div>}
                      </div>

                      <button 
                        type="submit" 
                        className="btn w-100 py-3 fw-bold mt-2"
                        disabled={isSubmitting}
                        style={{
                          background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)',
                          color: 'white',
                          borderRadius: '10px',
                          border: 'none',
                          transition: 'all 0.3s',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #6d0000 0%, #8B0000 100%)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending Message...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-2"></i>
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="p-4 rounded-4 shadow-sm" style={{ 
              backgroundColor: 'rgba(139, 0, 0, 0.03)', 
              borderLeft: '4px solid #8B0000' 
            }}>
              <h5 className="mb-4" style={{ color: '#8B0000' }}>
                <i className="fas fa-clock me-2"></i>
                Response Time & Availability
              </h5>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-business-time" style={{ color: '#8B0000' }}></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Business Hours</h6>
                      <p className="small text-muted mb-0">Mon-Fri: 9AM - 6PM<br/>Sat: 10AM - 2PM</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-reply" style={{ color: '#8B0000' }}></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Response Time</h6>
                      <p className="small text-muted mb-0">Within 24 hours during business days</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="fas fa-headset" style={{ color: '#8B0000' }}></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Support</h6>
                      <p className="small text-muted mb-0">Dedicated customer service team</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Font Awesome for icons */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
    </div>
  );
};

export default Contact;